import React, { useEffect, useState } from 'react';
import { GripVertical as GripVerticalIcon, Pencil as PencilIcon, Trash2, SplitIcon, ArrowRight } from 'lucide-react';
import { getUniqueDropdownOptions } from '../../../../../helper_functions';
import { useApiData } from '../../../../../contexts/apiService';
import Dropdown from '../../../../common/dropdown/dropdown';
import TextInput from '../../../../common/textInput/textInput';
import LabelTooltip from '../../../../common/label_tooltip/labelTooltip';
import Checkbox from '../../../../common/checkbox/checkbox';
import { CarrierTicketData, CrudeScheduleDelivery } from '../../../../../contexts/tableDefinitions';
import './sequenceItem.css'
import { trackEvent } from '../../../../../contexts/userAnalytics';

interface SequenceItemProps {
    type: 'pickup' | 'delivery';
    parentId?: number;
    totalVolume?: number;
    item_index: number;
    data: any;
    onEditStart: (type: string, item_index: number, itemId: number, isEditing: boolean) => void; // callback prop
    onLoadStart: (isLoading: boolean) => void; // callback
    isAnyItemEditing: boolean;
    dropdownOptions?: { value: string; label: string }[];
    canDelete?: boolean; // can only delete if sequence is max value
}

const SequenceItem: React.FC<SequenceItemProps> = ({ 
    type,
    parentId,
    totalVolume,
    item_index,
    data,
    onEditStart,
    onLoadStart,
    isAnyItemEditing,
    dropdownOptions,
    canDelete,
}) => {

    const { apiData, handleEditOrder, handleDeleteRow, handleEditReroute } = useApiData();
    const [isEditingItem, setIsEditingItem] = useState<boolean>(false);
    const [isEditingAnyItem, setIsEditingAnyItem] = useState<boolean>(false);
    const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);

    const [tableName, setTableName] = useState<string>();
    const [locationLabel, setLocationLabel] = useState<string>();
    const [locationID, setLocationID] = useState<string>();
    const [locationSecondaryLabel, setLocationSecondaryLabel] = useState<string>();
    const [marketLabel, setMarketLabel] = useState<string>();
    const [locationCoordinates, setLocationCoordinates] = useState<string>();
    const [locationProductType, setLocationProductType] = useState<string>();

    const [dropdownOptionsSecondary, setDropdownOptionsSecondary] = useState<{ value: string; label: string }[]>([]);
    const [dropdownPlaceholder, setDropdownPlaceholder] = useState<string>();

    const [dropdownMarketOptions, setDropdownMarketOptions] = useState<{ value: string; label: string }[]>([]);
    const [marketPlaceholder, setMarketPlaceholder] = useState<string>();

    const [textInputValue, setTextInputValue] = useState<string>();
    const [textInputPlaceholder, setTextInputPlaceholder] = useState<string>();

    const [primaryMismatch, setPrimaryMismatch] = useState<boolean>(false);
    const [primaryTooltip, setPrimaryTooltip] = useState<string>();

    const [secondaryMismatch, setSecondaryMismatch] = useState<boolean>(false);
    const [secondaryTooltip, setSecondaryTooltip] = useState<string>();

    const [tertiaryMismatch, setTertiaryMismatch] = useState<boolean>(false);
    const [tertiaryTooltip, setTertiaryTooltip] = useState<string>();

    const [isRerouted, setIsRerouted] = useState<boolean>(false);
    const [rerouteFrom, setRerouteFrom] = useState<string>();

    const [isBilledTo, setIsBilledTo] = useState<boolean>(false);
    const [billedTo, setBilledTo] = useState<string>();

    const [enableVolumeAdjustment, setEnableVolumeAdjustment] = useState<boolean>(false);

    const [inputError, setInputError] = useState<boolean>(false);
    const [inputErrorMessage, setInputErrorMessage] = useState<string>('');
    
    // setting initial states
    useEffect(() => {
        if (type === 'pickup') {
            handlePickupSetup();
        } else if (type === 'delivery') {
            handleDeliverySetup();
        }
    }, [isEditingItem, data]);

    // Separate handler for pickup setup
    const handlePickupSetup = () => {
        setTableName('crude_schedule_pickup');
        setLocationID(data.lease_id);
        setLocationLabel(data.lease_name);
        setLocationSecondaryLabel(data.tank_name);
        setTextInputValue(data.ticket_number || '');
        setTextInputPlaceholder('Assign Ticket Number');
        setLocationCoordinates(data.origin_coordinates);
        setLocationProductType(data.product_type);
        setBilledTo(data.billed_lease_id);
        setIsBilledTo(data.billed_lease_id ? true : false);

        // Set tank options
        if (data.lease_id) {
            const tankOptions = getUniqueDropdownOptions({
                tableData: apiData?.crude_tanks_definition,
                valueColumn: 'tank_name',
                labelColumn: 'tank_name',
                filterBy: { column: 'lease_id', value: data.lease_id },
                includeNullOption: true,
            });
            setDropdownOptionsSecondary(tankOptions);
            setDropdownPlaceholder(tankOptions?.length > 0 ? 'Select Tank' : 'No Tanks Available');
        }
        
        // Check mismatches
        checkPickupMismatches();
    };

    // Separate handler for delivery setup
    const handleDeliverySetup = () => {
        setTableName('crude_schedule_delivery');
        setLocationID(data.destination_id);
        setLocationLabel(data.destination_name);
        setLocationSecondaryLabel(data.offload_lane);
        setMarketLabel(data.market_name);
        setIsRerouted(data.is_reroute);
        setRerouteFrom(data.reroute_name);
        setLocationCoordinates(data.destination_coordinates);

        // Handle volume assignment
        const assignedVolume = assignDeliveryVolume();
        setTextInputValue(assignedVolume);
        setTextInputPlaceholder('Assign Offload Volume');
        
        // Set offload lane and market options
        if (data.destination_id) {
            const offloadLaneOptions = getUniqueDropdownOptions({
                tableData: apiData?.market_coordinates,
                valueColumn: 'offload_lane',
                labelColumn: 'offload_lane',
                filterBy: { column: 'id', value: data.destination_id },
                isJsonColumn: true,
                includeNullOption: true,
            });
            setDropdownOptionsSecondary(offloadLaneOptions);
            setDropdownPlaceholder(offloadLaneOptions?.length > 0 ? 'Select Offload Lane' : 'No Lanes Available');

            const marketOptions = getUniqueDropdownOptions({
                tableData: apiData?.market_discounts,
                valueColumn: 'market',
                labelColumn: 'market',
                filterBy: { column: 'station_id', value: data.destination_id },
                includeNullOption: true,
            });
            setDropdownMarketOptions(marketOptions);
            setMarketPlaceholder(marketOptions?.length > 0 ? 'Select Market' : 'No Markets Available');
        }
        
        // Check mismatches
        checkDeliveryMismatches();
    };

    // Check pickup mismatches
    const checkPickupMismatches = () => {
        // Primary: Lease mismatch
        if (data.has_carrier_match && (data.carrier_lease !== data.lease_name && data.carrier_lease !== data.billed_lease_name)) {
            setPrimaryMismatch(true);
            setPrimaryTooltip(`Carrier data shows lease: ${data.carrier_lease}`);
        } else {
            setPrimaryMismatch(false);
        }
        
        // Secondary: Tank mismatch
        // if (data.has_carrier_match && data.carrier_tank && data.carrier_tank !== data.tank_name) {
        //     setSecondaryMismatch(true);
        //     setSecondaryTooltip(`Carrier data shows: ${data.carrier_tank}`);
        // } else {
        //     setSecondaryMismatch(false);
        // }
        
        // Tertiary: Ticket not found
        if (!data.has_carrier_match && data.ticket_number) {
            setTertiaryMismatch(true);
            setTertiaryTooltip(`Ticket number not found in carrier data`);
        } else {
            setTertiaryMismatch(false);
        }
    };

    // Assign delivery volume based on carrier ticket data
    const assignDeliveryVolume = (): string => {
        if (!data.has_carrier_match) {
            setEnableVolumeAdjustment(true);
            return data.sequence === 1 && totalVolume && !data.gross_offload 
                ? `${totalVolume}` 
                : data.gross_offload;
        }
        
        try {
            const parentRow = apiData?.schedule_table?.find((row: any) => row.id === parentId);
            if (!parentRow?.carrier_ticket_data) {
                setEnableVolumeAdjustment(true);
                return data.gross_offload;
            }
            
            const carrierTicketData: CarrierTicketData = typeof parentRow.carrier_ticket_data === 'string' 
                ? JSON.parse(parentRow.carrier_ticket_data)
                : parentRow.carrier_ticket_data;
                
            const deliveryData: CrudeScheduleDelivery[] = typeof parentRow.delivery_data === 'string'
                ? JSON.parse(parentRow.delivery_data || '[]')
                : parentRow.delivery_data || [];
            
            const matchedTickets = Object.entries(carrierTicketData).filter(
                ([_, ticketInfo]: [string, any]) => ticketInfo.has_carrier_match === 1
            );
            
            if (matchedTickets.length === 0 || deliveryData.length === 0) {
                setEnableVolumeAdjustment(true);
                return data.gross_offload;
            }
            
            const numPickups = matchedTickets.length;
            const numOffloads = deliveryData.length;
            
            // Determine volume assignment based on pickup/offload ratio
            const result = getVolumeAssignment(matchedTickets, numPickups, numOffloads);
            
            // For sequence 1, subtract assigned volume from other deliveries
            if (data.sequence === 1 && result.assigned && result.volume) {
                const otherDeliveriesVolume = deliveryData
                    .filter(delivery => delivery.sequence !== 1 && delivery.gross_offload)
                    .reduce((sum: number, delivery) => {
                        const volumeValue = delivery.gross_offload || '0';
                        return sum + parseFloat(String(volumeValue));
                    }, 0);
                
                const adjustedVolume = parseFloat(result.volume) - otherDeliveriesVolume;
                const roundedVolume = Math.round(adjustedVolume * 100) / 100;
                
                setEnableVolumeAdjustment(!result.assigned);
                return roundedVolume > 0 ? roundedVolume.toString() : result.volume;
            }
            
            setEnableVolumeAdjustment(!result.assigned);
            return result.volume || data.gross_offload;
            
        } catch (error) {
            console.error('Error processing carrier ticket volumes:', error);
            setEnableVolumeAdjustment(true);
            return data.gross_offload;
        }
    };

    // Get volume assignment based on pickup/offload configuration
    const getVolumeAssignment = (
        matchedTickets: [string, any][], 
        numPickups: number, 
        numOffloads: number
    ): { volume: string | null, assigned: boolean } => {
        
        // Case 1: Multiple pickups, one delivery - sum if all destinations match
        if (numPickups > 1 && numOffloads === 1 && data.sequence === 1) {
            const allMatch = matchedTickets.every(([_, info]: [string, any]) => 
                info.destination === data.destination_name
            );
            if (allMatch) {
                const total = matchedTickets.reduce((sum, [_, info]: [string, any]) => 
                    sum + parseFloat(info.gross_barrels || '0'), 0
                );
                return { volume: total.toString(), assigned: true };
            }
        }
        
        // Case 2: Equal pickups and offloads - match by sequence
        if (numPickups === numOffloads) {
            const match = matchedTickets.find(([_, info]: [string, any]) => 
                info.pickup_sequence === data.sequence && info.destination === data.destination_name
            );
            if (match) {
                return { volume: match[1].gross_barrels, assigned: true };
            }
        }
        
        // Case 3: More offloads than pickups - match by destination
        if (numOffloads > numPickups) {
            const match = matchedTickets.find(([_, info]: [string, any]) => 
                info.destination === data.destination_name &&
                data.sequence === info.pickup_sequence
            );
            if (match) {
                return { volume: match[1].gross_barrels, assigned: true };
            }
        }
        
        return { volume: null, assigned: false };
    };

    // Check delivery destination mismatches
    const checkDeliveryMismatches = () => {
        if (!data.has_carrier_match || data.has_carrier_match !== 1) {
            setPrimaryMismatch(false);
            setSecondaryMismatch(false);
            setTertiaryMismatch(false);
            return;
        }
        // Primary: Destination mismatch
        checkDestinationMismatch();
        
        // Secondary: Offload lane mismatch
        if (data.carrier_offload_lane !== data.offload_lane && data.sequence === 1) {
            // setSecondaryMismatch(true);
            setSecondaryTooltip(`Carrier data shows offload lane: ${data.carrier_offload_lane}`);
            // temporarily disabling offload lane mismatch flag until data comes through in reports
            setSecondaryMismatch(false);
        } else {
            setSecondaryMismatch(false);
        }
        // Tertiary: Secondary offload without volume
        if (data.sequence !== 1 && !data.gross_offload && enableVolumeAdjustment) {
            setTertiaryMismatch(true);
            setTertiaryTooltip(`Secondary offloads require volume assignment`);
        } else {
            setTertiaryMismatch(false);
        }
    };

    // Check for destination mismatches between carrier tickets and deliveries
    const checkDestinationMismatch = () => {
        try {
            const parentRow = apiData?.schedule_table?.find((row: any) => row.id === parentId);
            if (!parentRow?.carrier_ticket_data) {
                setPrimaryMismatch(false);
                return;
            }
            
            const carrierTicketData: CarrierTicketData = typeof parentRow.carrier_ticket_data === 'string' 
                ? JSON.parse(parentRow.carrier_ticket_data)
                : parentRow.carrier_ticket_data;
                
            const deliveryData: CrudeScheduleDelivery[] = typeof parentRow.delivery_data === 'string'
                ? JSON.parse(parentRow.delivery_data || '[]')
                : parentRow.delivery_data || [];
            
            const matchedTickets = Object.entries(carrierTicketData).filter(
                ([_, ticketInfo]: [string, any]) => ticketInfo.has_carrier_match === 1
            );
            
            if (matchedTickets.length === 0 || deliveryData.length === 0) {
                setPrimaryMismatch(false);
                return;
            }
            
            const numPickups = matchedTickets.length;
            const numOffloads = deliveryData.length;
            
            // Find mismatches based on configuration
            const mismatches = findDestinationMismatches(matchedTickets, numPickups, numOffloads);
            
            if (mismatches.length > 0) {
                const ticketInfo = mismatches.map(([ticketNum, info]: [string, any]) => 
                    `Ticket ${ticketNum}: ${info.destination}`
                ).join(', ');
                setPrimaryMismatch(true);
                setPrimaryTooltip(`Carrier ticket destination mismatch - ${ticketInfo}`);
            } else {
                setPrimaryMismatch(false);
            }
            
        } catch (error) {
            console.error('Error checking destination mismatch:', error);
            setPrimaryMismatch(false);
        }
    };

    // Find which tickets have destination mismatches for current delivery
    const findDestinationMismatches = (
        matchedTickets: [string, any][], 
        numPickups: number, 
        numOffloads: number
    ): [string, any][] => {
        
        return matchedTickets.filter(([ticketNumber, ticketInfo]: [string, any]) => {
            const pickupSequence = ticketInfo.pickup_sequence;
            const carrierDestination = ticketInfo.destination;
            
            let shouldCompare = false;
            
            // Determine if this ticket should be compared to current delivery
            if (numPickups > 1 && numOffloads === 1) {
                shouldCompare = data.sequence === 1;
            } else if (numPickups === numOffloads) {
                shouldCompare = pickupSequence === data.sequence;
            } else if (numOffloads > numPickups) {
                shouldCompare = carrierDestination === data.destination_name;
            }
            
            return shouldCompare && carrierDestination !== data.destination_name;
        });
    };
    
    const handleMarketChange = async (selectedValue: string) => {  
        let currentValue = marketLabel;

         if (!tableName) {
            console.error('required values not set');
            return;
        }

        if (!currentValue) {
            currentValue = '';
        }

        try {
            setIsLoadingEdit(true);
            onLoadStart(true);
            const [success, response] = await handleEditOrder(tableName, data.id, 'market_name', currentValue, selectedValue, parentId);
            
            if (!success) {
                console.error(`edit failed`);
                if (response && response.status) {
                    alert(`Failed to submit edit - ${response.statusText}`);
                }
            }
        } catch (error) {;
            alert(`Error updating value`);
        }
        setIsLoadingEdit(false);
        onLoadStart(false);
        // Exit edit mode
        setIsEditingItem(false);

        // Notify parent that editing has stopped
        if (onEditStart) {
            onEditStart(type, item_index, data.id, false);
        }
    }

    // handling dropdown edits
    const handleDropdownChange = async (selectedValue: string, dropdownType: 'location' | 'secondary') => {      
        
        let currentValue = dropdownType === 'location' ? locationID : locationSecondaryLabel;
        
        if (!tableName) {
            console.error('required values not set');
            return;
        }

        if (!currentValue) {
            currentValue = '';
        }

        const editedCol = dropdownType === 'location' 
            ? (type === 'pickup' ? 'lease_id' : 'destination_id')
            : (type === 'pickup' ? 'tank_name' : 'offload_lane');

        try {
            setIsLoadingEdit(true);
            onLoadStart(true);
            const [success, response] = await handleEditOrder(tableName, data.id, editedCol, currentValue, selectedValue, parentId);

            if (!success) {
                console.error(`edit failed`);
                if (response && response.status) {
                    alert(`Failed to submit edit - ${response.statusText}`);
                }
            }
        } catch (error) {;
            alert(`Error updating value`);
        }
        setIsLoadingEdit(false);
        onLoadStart(false);
        // Exit edit mode
        setIsEditingItem(false);
        trackEvent('edit_cell', { tableName: tableName, colName: editedCol });

        // Notify parent that editing has stopped
        if (onEditStart) {
            onEditStart(type, item_index, data.id, false);
        }
    };

    // Handle text input value changes (without submitting)
    const handleTextInputValueChange = (value: string) => {
        setTextInputValue(value);

        // Validate input for delivery type
        if (type === 'delivery') {
            const newVolume = parseFloat(value);
            if (totalVolume && (totalVolume + data.gross_offload) < newVolume) { 
                setInputError(true);
                setInputErrorMessage(`Volume cannot exceed ${(totalVolume + data.gross_offload)} bbls (total load volume)`);
            } else {
                setInputError(false);
                setInputErrorMessage('');
            }
        }
    };
    
    // handling text input edits
    const handleInputChange = async (selectedValue: string) => {   
        let currentValue = type === 'pickup' ? data.ticket_number : data.gross_offload;

        if (!tableName) {
            console.error('required values not set');
            return;
        }

        if (!currentValue) {
            currentValue = '';
        }

        if (inputError) {
            alert(`Fix input errors before submitting.`);
            return;
        }
 
        const editedCol = type === 'pickup' ? 'ticket_number' : 'gross_offload';
        
        try {
            setIsLoadingEdit(true);
            onLoadStart(true);
            const [success, response] = await handleEditOrder(tableName, data.id, editedCol, currentValue, selectedValue, parentId);
            
            if (!success) {
                console.error(`edit failed`);
                if (response) {
                    alert(`Failed to submit edit - ${response}`);
                }            
            }
        } catch (error) {;
            alert(`Error updating value`);
        }
        setIsLoadingEdit(false);
        onLoadStart(false);
        // Exit edit mode
        setIsEditingItem(false);
        trackEvent('edit_cell', { tableName: tableName, colName: editedCol });
        
        // Notify parent that editing has stopped
        if (onEditStart) {
            onEditStart(type, item_index, data.id, false);
        }
    }

    // pressing ESC key will exit edit mode
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isEditingItem) {
                event.preventDefault();
                handleExitEdit();
            }
        };
        // if editing enable listener
        if (isEditingItem) {
            document.addEventListener('keydown', handleKeyDown);
        }

        // cleanup listener
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isEditingItem]);

    const handleEditClick = () => {
        const newEditingState = !isEditingItem;
        setInputError(false);
        setInputErrorMessage('');
        setIsEditingItem(newEditingState);
        setIsEditingAnyItem(true)
        // Notify parent component
        if (onEditStart) {
            onEditStart(type, item_index, data.id, newEditingState);
        }
    };

    const handleExitEdit = () => {
        setIsEditingItem(false);
        setInputError(false);
        setInputErrorMessage('');
        // Notify parent that editing has stopped
        if (onEditStart) {
            onEditStart(type, item_index, data.id, false);
        }
    };

    const handleDeleteClick = async () => {
        if (!tableName) {
            console.error('Table name is not set');
            return;
        }
        if (!canDelete) {
            console.log('cannot delete non-max sequence item');
            return;
        } else {
            // Notify parent that editing has started
            if (onEditStart) {
                onEditStart(type, item_index, data.id, true);
            }
            setIsLoadingEdit(true);
            onLoadStart(true);
            
            const success = await handleDeleteRow(new Set([data.id]), tableName, parentId);
            if (!success) {
                console.error(`deletion failed`);
                alert(`Failed to submit deletion`);
            }
            // Notify parent that editing has stopped
            if (onEditStart) {
                onEditStart(type, item_index, data.id, false);
            }
            setIsLoadingEdit(false);
            onLoadStart(false);
        }
    }
    
    // Handle reroute checkbox change
    const handleRerouteChange = (checked: boolean) => {
        setIsRerouted(checked);
    };

    const handleUndoReroute = async (checked: boolean) => {
        try {
            setIsLoadingEdit(true);
            onLoadStart(true);
            const [success, response] = await handleEditOrder('crude_schedule_delivery', data.id, 'is_reroute', 1, 0, parentId);
            
            if (!success) {
                console.error(`edit failed`, response && response.status);
                if (response && response.status) {
                    alert(`Failed to submit edit - ${response.statusText}`);
                }
            }
        } catch (error) {;
            alert(`Error updating value`);
        }
        setIsLoadingEdit(false);
        onLoadStart(false);
        // Exit edit mode
        setIsEditingItem(false);

        // Notify parent that editing has stopped
        if (onEditStart) {
            onEditStart(type, item_index, data.id, false);
        }
    };

    // Handle reroute dropdown change
    const handleReroute = async (selectedValue: number) => {

        const editData = {
            row_id: data.id,
            current_destination_id: data.destination_id,
            reroute_to: selectedValue,
            current_is_reroute: isRerouted ? 1 : 0,
            parent_id: parentId,
        };
        
        if (selectedValue) {
            try {
                setIsLoadingEdit(true);
                onLoadStart(true);
                const success = await handleEditReroute('crude_schedule_delivery', editData);
                
                if (!success) {
                    console.error(`reroute edit failed`);
                    alert(`Failed to submit reroute`);
                }
            } catch (error) {
                alert(`Error updating reroute`);
            }
            setIsLoadingEdit(false);
            onLoadStart(false);
            // Exit edit mode
            setIsEditingItem(false)

            // Notify parent that editing has stopped
            if (onEditStart) {
                onEditStart(type, item_index, data.id, false);
            }
        }
    };
    
    const handleBillingChange = async (selectedValue: number) => {
        if (selectedValue) {
            try {
                setIsLoadingEdit(true);
                onLoadStart(true);
                const [success, response] = await handleEditOrder('crude_schedule_pickup', data.id, 'billed_lease_id', data.billed_lease_id, selectedValue, parentId);
                
                if (!success) {
                    console.error(`billing edit failed`);
                    if (response && response.status) {
                        alert(`Failed to submit billing edit - ${response.statusText}`);
                    }
                }
            } catch (error) {
                alert(`Error updating billing`);
            }
            setIsLoadingEdit(false);
            onLoadStart(false);
            // Exit edit mode
            setIsEditingItem(false)

            // Notify parent that editing has stopped
            if (onEditStart) {
                onEditStart(type, item_index, data.id, false);
            }
        }
    }
    
    return (
        <div
            className={`sequenceItem-mainContainer ${
                isEditingItem ? 'sequenceItem-mainContainer-editing' :  ''}
            } ${
                !isEditingItem && isAnyItemEditing ? 'sequenceItem-mainContainer-disabled' : ''
            }`}
        >
            <div className='sequenceItem-topRow'>
                <div
                    className={`sequenceItem-gripButton ${
                    isEditingItem ? 'sequenceItem-gripButton-editing' :  ''}
                } ${
                    !isEditingItem && isAnyItemEditing ? 'sequenceItem-gripButton-disabled' : ''
                }`}
                    >
                    <GripVerticalIcon 
                        size={16} 
                        style={{color: 'var(--color-grey-light-text)'}}
                    />
                </div>
                <div className="sequenceItem-labelContainer">
                    <span className="sequenceItem-sequenceNumber">
                        {data.sequence}.
                    </span>
                    <LabelTooltip
                        tooltip={primaryMismatch ? primaryTooltip : undefined}
                        showTooltip={primaryMismatch}
                        onTooltipClick={() => {
                            console.log('Tooltip clicked!');
                        }}
                    >
                        <span className={`sequenceItem-location ${
                            primaryMismatch ? 'sequenceItem-warningLabel' : ''
                        }`}>
                            {locationLabel}
                        </span>
                    </LabelTooltip>
                    <LabelTooltip
                        tooltip={secondaryMismatch ? secondaryTooltip : undefined}
                        showTooltip={secondaryMismatch}
                        onTooltipClick={() => {
                            console.log('Tooltip clicked!');
                        }}
                    >
                        <span className={`sequenceItem-locationSecondary ${
                            secondaryMismatch ? 'sequenceItem-warningLabel' : ''
                        }`}>
                            {(secondaryMismatch && !locationSecondaryLabel) ? (
                                'no value'
                            ) : locationSecondaryLabel}
                        </span>
                    </LabelTooltip>
                    <LabelTooltip
                        tooltip={tertiaryMismatch ? tertiaryTooltip : undefined}
                        showTooltip={tertiaryMismatch}
                    >
                        <span className={`sequenceItem-thirdText ${
                            tertiaryMismatch ? 'sequenceItem-warningLabel' : ''
                        }`}>
                            {tertiaryMismatch 
                                ? (type === 'pickup' ? textInputValue : 'no volume assigned')
                                : textInputValue != null 
                                    ? `${textInputValue}${(type === 'delivery' && textInputValue != null) ? ' bbls' : ''}`
                                    : ''
                            }
                        </span>
                    </LabelTooltip>
                </div>
                <div className="sequenceItem-buttonContainer">
                    <div
                        className="sequenceItem-editButton"
                        onClick={handleEditClick}
                    >
                        <PencilIcon
                            size={16} 
                            style={{color: 'var(--color-grey-light-text)'}}
                        />
                    </div>
                    <div
                        className={`sequenceItem-deleteButton ${!canDelete ? 'sequenceItem-deleteButton-disabled' : ''}`}
                        onClick={canDelete ? handleDeleteClick : undefined}
                    > 
                        <Trash2
                            size={16} 
                            style={{color: !canDelete ? 'var(--color-grey-light-darker)' : 'var(--color-grey-light-text)'}}
                        />
                    </div>
                </div>
            </div>
            
            {type === 'pickup' && data.origin_coordinates && (
                <div className='sequenceItem-leaseCoordinates'>
                    <div>
                        {locationCoordinates}
                    </div>
                    <div>
                        {locationProductType}
                    </div>
                    {data.gross_barrels != null && (
                        <div>
                            {data.gross_barrels} bbls
                        </div>
                    )}
                </div>
            )}

            {type === 'pickup' && billedTo &&(
                <div className='sequenceItem-billedTo'>
                    <div>
                        {`Billed to`}
                    </div>
                    <ArrowRight
                        size={16} 
                    />
                    <div className='sequenceItem-billedToLeaseName'>
                        {data.billed_lease_name}
                    </div>
                </div>
            )}

            {type === 'delivery' && data.market_name && (
                <div className='sequenceItem-leaseCoordinates'>
                    <div>
                        {marketLabel}
                    </div>
                </div>
            )}

            {type === 'delivery' && data.is_reroute === 1 && (
                <div className='sequenceItem-rerouteLabel'>
                    <SplitIcon
                        size={14}
                        style={{ transform: 'rotate(90deg)', color: 'var(--color-blue-default)' }}
                    />
                    <div style={{ color: 'var(--color-grey-medium)' }}>
                        {data.reroute_name}
                    </div>
                </div>
            )}

            {isEditingItem && (
                <div className="sequenceItem-editingContainer">
                    <Dropdown
                        options={dropdownOptions}
                        value={locationID}
                        onChange={(value) => handleDropdownChange(value, 'location')}
                        height={26}
                        className='dropdown-inputMatch'
                        isDisabled={isLoadingEdit}
                    />
                    <Dropdown
                        options={dropdownOptionsSecondary}
                        value={locationSecondaryLabel}
                        onChange={(value) => handleDropdownChange(value, 'secondary')}
                        height={26}
                        className='dropdown-inputMatch'
                        placeholder={dropdownPlaceholder}
                        isDisabled={isLoadingEdit}
                    />
                    {(type === 'delivery') && (
                        <Dropdown
                            options={dropdownMarketOptions}
                            value={marketLabel}
                            onChange={(value) => handleMarketChange(value)}
                            height={26}
                            className='dropdown-inputMatch'
                            placeholder={marketPlaceholder}
                            isDisabled={isLoadingEdit}
                        />
                    )}
                    {(data.sequence !== 1 && type === 'delivery' && enableVolumeAdjustment) && (
                        <TextInput
                            value={textInputValue}
                            onChange={handleTextInputValueChange}
                            onEnter={handleInputChange}
                            width={200}
                            height={26}
                            disabled={isLoadingEdit}
                            placeholder={textInputPlaceholder}
                            showError={inputError}
                            errorMessage={inputErrorMessage}
                        />
                    )}
                    {(type === 'pickup') && (
                        <>
                            <TextInput
                                value={textInputValue}
                                onChange={handleTextInputValueChange}
                                onEnter={handleInputChange}
                                width={200}
                                height={26}
                                disabled={isLoadingEdit}
                                placeholder={textInputPlaceholder}
                            />
                            <div className='sequenceItem-rerouteCheckbox'>
                                <Checkbox
                                    label={'Bill to'}
                                    size={14}
                                    checked={isBilledTo}
                                    onChange={(checked) => setIsBilledTo(checked)}
                                    isDisabled={isLoadingEdit}
                                />
                                {isBilledTo ? (
                                    <Dropdown
                                        options={dropdownOptions}
                                        value={billedTo}
                                        onChange={(value) => handleBillingChange(Number(value))}
                                        height={26}
                                        className='dropdown-inputMatch'
                                        isDisabled={isLoadingEdit}
                                    />
                                ) : null}
                            </div>
                        </>
                    )}
                    {(type === 'delivery' && data.sequence === 1 && data.is_reroute !== 1) && (
                        <div className='sequenceItem-rerouteCheckbox'>
                            <Checkbox
                                label={'Reroute'}
                                size={14}
                                checked={isRerouted}
                                onChange={handleRerouteChange}
                                isDisabled={isLoadingEdit}
                            />
                            {isRerouted && data.is_reroute !== 1 ? (
                                <Dropdown
                                    options={dropdownOptions}
                                    value={rerouteFrom}
                                    onChange={(value) => handleReroute(Number(value))}
                                    height={26}
                                    className='dropdown-inputMatch'
                                    isDisabled={isLoadingEdit}
                                />
                            ) : null}
                        </div>
                    )}
                    {(type === 'delivery' && data.sequence === 1 && data.is_reroute === 1) && (
                        <div className='sequenceItem-rerouteCheckbox'>
                            <Checkbox
                                label={'Undo reroute'}
                                size={14}
                                checked={isRerouted}
                                onChange={handleUndoReroute}
                                isDisabled={isLoadingEdit}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
};

export default SequenceItem;